// =============================================================================
// FILE: src/components/guards/ProtectedRoute.jsx
// MODULE: Security — Route Guards
// PROJECT: Arcus Sports Management
//
// DESCRIPTION:
//   ProtectedRoute — blocks unauthenticated users.
//   RoleGuard — blocks users whose role doesn't match required roles.
//
// KEY FIX:
//   Step 3 adds a "role loading" spinner between "session confirmed" and
//   "role available". This covers the race condition where LoginPage does
//   window.location.href = '/admin' before AuthProvider finishes loading
//   the profile. Without this, ProtectedRoute would see user=true, role=null
//   and redirect back to /login, causing the auth loop.
// =============================================================================

import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useAuth } from '@/providers/AuthProvider'
import { ROUTES, ROLES } from '@/lib/constants'
import { LoadingScreen } from '@/components/ui/LoadingScreen'

export function ProtectedRoute({ redirectTo = ROUTES.LOGIN }) {
  const { isAuthenticated, isLoading, isOnboarded, sessionChecked, role } = useAuth()
  const location = useLocation()

  // Step 1: Initial session check not done yet
  if (isLoading || !sessionChecked) {
    return <LoadingScreen message="Loading Arcus..." />
  }

  // Step 2: No session — go to login
  if (!isAuthenticated) {
    return (
      <Navigate
        to={redirectTo}
        state={{ from: location.pathname }}
        replace
      />
    )
  }

  // Step 3: Authenticated but profile still loading — wait briefly
  // sessionChecked = true means AuthProvider finished, so if role is STILL
  // null it means the user has no profile yet (new signup, confirmed email)
  // In that case redirect to onboarding immediately.
  if (sessionChecked && !role) {
  return <Navigate to={ROUTES.ONBOARDING} replace />
  }

  // If role is still loading (profile query in flight), show spinner
  if (!role) {
    return <LoadingScreen message="Loading your profile..." />
  }

  // Step 4: Not onboarded yet
  if (!isOnboarded && location.pathname !== ROUTES.ONBOARDING) {
    return <Navigate to={ROUTES.ONBOARDING} replace />
  }

  // Step 5: All good — render child routes
  return <Outlet />
}

export function RoleGuard({ roles }) {
  const { role } = useAuth()

  if (!role) return null

  const hasAccess = roles.includes(role)
  if (!hasAccess) {
    return <Navigate to={getRoleFallbackRoute(role)} replace />
  }

  return <Outlet />
}

function getRoleFallbackRoute(role) {
  switch (role) {
    case ROLES.SUPER_ADMIN: return ROUTES.SUPER_ADMIN
    case ROLES.ADMIN:       return ROUTES.ADMIN_DASHBOARD
    case ROLES.TEAM_LEADER: return ROUTES.PLAYER_DASHBOARD
    case ROLES.PLAYER:      return ROUTES.PLAYER_DASHBOARD
    default:                return ROUTES.LOGIN
  }
}
